<template>
  <div class="breadcrumb-bar" v-if="showBreadcrumb">
    <div class="breadcrumb-inner">
      <div v-for="(step, index) in steps" :key="index" class="breadcrumb-item">
        <!-- Separador -->
        <span v-if="index > 0" class="breadcrumb-sep">›</span>

        <!-- Chip del paso -->
        <div
          class="breadcrumb-chip"
          :class="[
            step.status,
            { clickable: step.status === 'done' && step.target },
          ]"
          @click="
            step.status === 'done' &&
              step.target &&
              $emit('navigate-back', step.target)
          "
        >
          <span class="chip-text">{{ step.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProductionBreadcrumb",

  props: {
    currentView: {
      type: Number,
      required: true,
    },
    tipoSeleccionado: {
      type: String,
      default: null, // 'COOPERATIVE' | 'COMMERCIAL'
    },
    fruitType: {
      type: String,
      default: null,
    },
    finca: {
      type: Object,
      default: null,
    },
    zona: {
      type: Object,
      default: null,
    },
    lomo: {
      type: Number,
      default: null,
    },
    variedad: {
      type: String,
      default: null,
    },
    zoneStep: {
      type: String,
      default: "zone",
    },
    formato: {
      type: Object,
      default: null,
    },
    modo: {
      type: [String, Object],
      default: null,
    },
    cooperativeStep: {
      type: String,
      default: "mode",
    },
    categoria: {
      type: [Object, String],
      default: null,
    },
    countWidgetStep: {
      type: String,
      default: null,
    },
  },

  computed: {
    showBreadcrumb() {
      return this.currentView >= 2 && this.currentView <= 4;
    },

    // Normalizers for modo: works whether prop is a string code or a full mode object
    modoCode() {
      if (!this.modo) return null;
      return typeof this.modo === "object" ? this.modo.code : this.modo;
    },
    modoRequiresFormat() {
      if (typeof this.modo === "object") return !!this.modo.requires_format;
      return this.modo === "WEIGHT";
    },
    modoRequiresFruitCount() {
      if (typeof this.modo === "object")
        return !!this.modo.requires_fruit_count;
      return this.modo === "COUNT" || this.modo === "LOTE";
    },

    categoriaName() {
      if (!this.categoria) return null;
      if (typeof this.categoria === "object")
        return this.categoria.name || this.categoria.code || "?";
      // String fallback
      return this.categoria;
    },
    categoriaCode() {
      if (!this.categoria) return null;
      if (typeof this.categoria === "object")
        return this.categoria.code || null;
      return this.categoria;
    },
    categoriaHasSubcategories() {
      if (!this.categoria || typeof this.categoria !== "object") return false;
      return (
        Array.isArray(this.categoria.subcategories) &&
        this.categoria.subcategories.some((value) => String(value || "").trim())
      );
    },
    isDefectChecklistCategory() {
      return (
        this.categoriaHasSubcategories ||
        this.categoriaCode === "OTROS_DEFECTOS"
      );
    },
    modoLabel() {
      if (!this.modo) return "Modo";
      if (typeof this.modo === "object") return this.modo.name || "Modo";
      if (this.modo === "WEIGHT") return "Peso";
      if (this.modo === "COUNT") return "Conteo";
      if (this.modo === "LOTE") return "Lote";
      return this.modo;
    },

    tipoLabel() {
      if (this.tipoSeleccionado === "COOPERATIVE") return "Experimental";
      if (this.tipoSeleccionado === "COMMERCIAL") return "Socios";
      return null;
    },
    fruitLabel() {
      if (this.fruitType === "FRESA") return "Fresa";
      if (this.fruitType === "ARANDANO") return "Arándano";
      if (this.fruitType === "ZARZAMORA") return "Zarzamora";
      if (this.fruitType === "FRAMBUESA") return "Frambuesa";
      return null;
    },

    steps() {
      const steps = [];
      const v = this.currentView;
      const isCooperative = this.finca?.farm_type === "COOPERATIVE";
      const hasZones =
        Array.isArray(this.finca?.zones) && this.finca.zones.length > 0;

      // ── Paso 0: Tipo (Experimental / Socios) ──────────────────────────────────
      if (this.tipoLabel) {
        steps.push({
          label: this.tipoLabel,
          status: "done",
          target: { view: 1, farmTypeStep: "type" },
        });
      }

      if (this.tipoSeleccionado === "COOPERATIVE" && this.fruitLabel) {
        steps.push({
          label: this.fruitLabel,
          status: "done",
          target: { view: 1, farmTypeStep: "fruit" },
        });
      }

      // ── Paso 1: Finca ───────────────────────────────────────────────────────
      steps.push({
        label: this.finca ? this.finca.name : "Parcela",
        status: v > 1 ? "done" : "current",
        target: v > 1 ? { view: 1, farmTypeStep: "farmland" } : null,
      });

      // ── Paso 2: Lomo (sub-paso cuando la finca tiene lomos) ──────────────
      if (hasZones && v === 2 && this.zoneStep === "lomo") {
        // Seleccionando lomo
        steps.push({ label: "Lomo", status: "current", target: null });
        return steps;
      }

      if (
        hasZones &&
        v === 2 &&
        this.zoneStep === "zone" &&
        this.lomo !== null
      ) {
        // Lomo ya seleccionado, ahora seleccionando zona
        steps.push({
          label: `Lomo ${this.lomo}`,
          status: "done",
          target: { view: 2, zoneStep: "lomo" },
        });
        steps.push({ label: "Zona", status: "current", target: null });
        return steps;
      }

      // ── Paso 2: Variedad (sub-paso cuando la finca usa variedades) ────────
      if (hasZones && v === 2 && this.zoneStep === "variety") {
        steps.push({ label: "Variedad", status: "current", target: null });
        return steps;
      }

      if (
        hasZones &&
        v === 2 &&
        this.zoneStep === "zone" &&
        this.variedad !== null
      ) {
        // Variedad ya seleccionada, ahora seleccionando zona
        steps.push({
          label: this.variedad,
          status: "done",
          target: { view: 2, zoneStep: "variety" },
        });
        steps.push({ label: "Zona", status: "current", target: null });
        return steps;
      }

      // ── Paso 2: Zona ───────────────────────────────────────────────
      if (hasZones && v >= 2) {
        // Si hay lomo, mostrarlo como paso completado antes de zona
        if (this.lomo !== null) {
          steps.push({
            label: `Lomo ${this.lomo}`,
            status: "done",
            target: v > 2 ? { view: 2, zoneStep: "lomo" } : null,
          });
        }
        // Si hay variedad, mostrarla como paso completado antes de zona
        if (this.variedad !== null) {
          steps.push({
            label: this.variedad,
            status: "done",
            target: v > 2 ? { view: 2, zoneStep: "variety" } : null,
          });
        }
        steps.push({
          label: this.zona ? this.zona.name : "Zona",
          status: v > 2 ? "done" : "current",
          target:
            v > 2
              ? {
                  view: 2,
                  zoneStep:
                    this.lomo !== null || this.variedad !== null
                      ? "zone"
                      : undefined,
                }
              : null,
        });
      }

      // ── Paso 3: Modo / Categoría / Formato ────────────────────
      if (v === 3) {
        if (isCooperative) {
          if (this.cooperativeStep === "mode") {
            steps.push({ label: "Modo", status: "current", target: null });
          } else if (this.cooperativeStep === "category") {
            steps.push({
              label: this.modoLabel,
              status: "done",
              target: { view: 3, cooperativeStep: "mode" },
            });
            steps.push({ label: "Categoría", status: "current", target: null });
          } else if (this.cooperativeStep === "format") {
            steps.push({
              label: this.modoLabel,
              status: "done",
              target: { view: 3, cooperativeStep: "mode" },
            });
            steps.push({
              label: this.categoriaName || "Categoría",
              status: "done",
              target: { view: 3, cooperativeStep: "category" },
            });
            steps.push({ label: "Formato", status: "current", target: null });
          }
        } else {
          // Commercial: direct to format
          steps.push({
            label: this.formato ? this.formato.name : "Formato",
            status: "current",
            target: null,
          });
        }
      }

      // ── Paso 3 completado (cuando estamos en vista 4) ─────────
      if (v === 4) {
        if (isCooperative) {
          steps.push({
            label: this.modoLabel,
            status: "done",
            target: { view: 3, cooperativeStep: "mode" },
          });
          if (this.categoriaName) {
            steps.push({
              label: this.categoriaName,
              status: "done",
              target: { view: 3, cooperativeStep: "category" },
            });
          }
          if (this.formato && this.modoRequiresFormat) {
            steps.push({
              label: this.formato.name,
              status: "done",
              target: { view: 3, cooperativeStep: "format" },
            });
          }
        } else {
          steps.push({
            label: this.formato ? this.formato.name : "Formato",
            status: "done",
            target: { view: 3 },
          });
        }

        // Sub-pasos internos del widget (COUNT / LOTE)
        if (this.modoRequiresFruitCount && this.modoCode !== "LOTE") {
          const countSteps = ["Peso total", "Núm. frutas"];
          const currentIdx =
            this.countWidgetStep === "Número de frutas" ? 1 : 0;
          countSteps.forEach((s, i) => {
            if (i < currentIdx)
              steps.push({ label: s, status: "done", target: null });
            else if (i === currentIdx)
              steps.push({ label: s, status: "current", target: null });
          });
        } else if (this.modoCode === "LOTE") {
          if (this.isDefectChecklistCategory) {
            steps.push({ label: "Defectos", status: "current", target: null });
            return steps;
          }

          const loteSteps = ["Peso total", "Fr./tarrina", "Tarrinas"];
          const loteMap = {
            "Peso total": 0,
            "Frutas por tarrina": 1,
            "Número de tarrinas": 2,
          };
          const currentIdx = loteMap[this.countWidgetStep] ?? 0;
          loteSteps.forEach((s, i) => {
            if (i < currentIdx)
              steps.push({ label: s, status: "done", target: null });
            else if (i === currentIdx)
              steps.push({ label: s, status: "current", target: null });
          });
        } else {
          // WEIGHT → paso único Cantidad
          steps.push({ label: "Cantidad", status: "current", target: null });
        }
      }

      return steps;
    },
  },
};
</script>

<style scoped>
.breadcrumb-bar {
  position: fixed;
  top: calc(
    50px + max(var(--cap-inset-top, 0px), env(safe-area-inset-top, 0px))
  );
  left: 0;
  right: 0;
  z-index: 999;
  height: auto;
  background: #ffffff;
  border-bottom: 1px solid rgba(16, 118, 49, 0.15);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.breadcrumb-inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 5px 12px;
  gap: 5px 4px;
  width: 100%;
  box-sizing: border-box;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 1;
  min-width: 0;
}

.breadcrumb-sep {
  color: #bdbdbd;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  user-select: none;
  flex-shrink: 0;
}

/* ── Chip base ─────────────────────────────────────────────── */
.breadcrumb-chip {
  display: flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  transition: background 0.2s ease, color 0.2s ease;
}

/* Paso completado */
.breadcrumb-chip.done {
  background: rgba(16, 118, 49, 0.1);
  border: 1px solid rgba(16, 118, 49, 0.25);
}

.breadcrumb-chip.done.clickable {
  cursor: pointer;
}

.breadcrumb-chip.done.clickable:active {
  background: rgba(16, 118, 49, 0.2);
}

.breadcrumb-chip.done .chip-text {
  color: #107631;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

/* Paso actual */
.breadcrumb-chip.current {
  background: linear-gradient(135deg, #107631 0%, #1a9440 100%);
  border: 1px solid transparent;
  box-shadow: 0 2px 6px rgba(16, 118, 49, 0.35);
}

.breadcrumb-chip.current .chip-text {
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.chip-text {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

/* ====== DESKTOP (>= 768px) ====== */
@media (min-width: 768px) {
  .breadcrumb-bar {
    height: 56px;
  }

  .breadcrumb-inner {
    flex-wrap: nowrap;
    padding: 0 28px;
    gap: 6px;
    height: 56px;
    align-items: center;
  }

  .breadcrumb-sep {
    font-size: 20px;
  }

  .breadcrumb-chip {
    padding: 7px 20px;
  }

  .breadcrumb-chip.done .chip-text,
  .breadcrumb-chip.current .chip-text {
    font-size: 14px;
  }

  .chip-text {
    max-width: 220px;
  }
}
</style>
